if (!customElements.get('alpe-hero-slider')) {
  customElements.define('alpe-hero-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.track = this.querySelector('.rd-hero-slider__track');
      this.track.querySelectorAll('[data-hero-clone]').forEach(clone => clone.remove());
      if (this.slides.length > 1) {
        const clone = slide => {
          const copy = slide.cloneNode(true);
          copy.removeAttribute('data-hero-slide');
          copy.removeAttribute('data-block-id');
          copy.removeAttribute('data-shopify-editor-block');
          copy.dataset.heroClone = 'true';
          copy.inert = true;
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
          copy.querySelectorAll('video').forEach(video => video.removeAttribute('autoplay'));
          return copy;
        };
        this.track.prepend(clone(this.slides.at(-1)));
        this.track.append(clone(this.slides[0]));
      }
      this.controls = this.querySelector('.rd-hero-slider__controls');
      this.toggle = this.querySelector('[data-hero-toggle]');
      this.counter = this.querySelector('[data-hero-counter]');
      this.status = this.querySelector('[data-hero-status]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.paused = this.motion.matches || this.dataset.autoplay !== 'true';
      this.focusPaused = false;
      this.hovered = false;
      this.visible = true;
      this.editorSelected = false;
      this.interval = Math.max(3000, Math.min(15000, Number(this.dataset.interval) || 6000));
      if (this.controls) this.controls.hidden = this.slides.length < 2;
      this.addEventListener('click', event => {
        if (event.target.closest('[data-hero-prev]')) this.go(this.index - 1, true);
        if (event.target.closest('[data-hero-next]')) this.go(this.index + 1, true);
        if (event.target.closest('[data-hero-toggle]')) {
          this.paused = !(this.toggleWasPaused ?? (this.paused || this.focusPaused));
          this.focusPaused = false;
          delete this.toggleWasPaused;
          this.schedule();
        }
        const videoToggle = event.target.closest('[data-hero-video-toggle]');
        if (videoToggle) {
          const slide = videoToggle.closest('[data-hero-slide]');
          const video = slide.querySelector('video');
          if (video.paused) {
            delete slide.dataset.videoPaused;
            video.play().then(() => this.updateVideoButton(slide)).catch(() => this.updateVideoButton(slide));
          } else {
            slide.dataset.videoPaused = 'true';
            video.pause();
            this.updateVideoButton(slide);
          }
        }
      }, {signal});
      this.addEventListener('keydown', event => {
        if (event.target !== this && !event.target.closest('.rd-hero-slider__controls')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
        if (event.target === this && event.code === 'Space') {
          event.preventDefault();
          this.paused = !this.paused;
          this.focusPaused = false;
          this.schedule();
        }
      }, {signal});
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = this.dataset.pauseOnHover !== 'false';
        this.schedule();
      }, {signal});
      this.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.schedule();
      }, {signal});
      this.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.schedule();
      }, {signal});
      this.addEventListener('focusout', event => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.schedule();
      }, {signal});
      this.addEventListener('pointerdown', event => {
        if (event.target.closest('[data-hero-toggle]')) this.toggleWasPaused = this.paused || this.focusPaused;
        if (event.pointerType === 'touch') return;
        this.startPoint = null;
        if (this.slides.length < 2 || event.target.closest('a, button, input')) return;
        const mouse = event.pointerType === 'mouse';
        if (mouse && (event.button !== 0 || this.dataset.mouseDrag === 'false')) return;
        if (!mouse && this.dataset.touchSwipe === 'false') return;
        if (mouse) event.preventDefault();
        this.settleLoop();
        this.startPoint = {x: event.clientX, y: event.clientY, id: event.pointerId, mouse};
        this.schedule();
      }, {signal});
      this.addEventListener('dragstart', event => {
        if (this.dataset.mouseDrag !== 'false' && !event.target.closest('a, button')) event.preventDefault();
      }, {signal});
      this.addEventListener('pointermove', event => {
        if (event.pointerType === 'touch' || !this.startPoint || event.pointerId !== this.startPoint.id) return;
        const x = event.clientX - this.startPoint.x;
        const y = event.clientY - this.startPoint.y;
        if (Math.abs(x) < 8 || Math.abs(x) < Math.abs(y) * 1.5) return;
        if (this.startPoint.mouse && !this.hasPointerCapture(event.pointerId)) this.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
        const distance = Math.max(-this.clientWidth, Math.min(this.clientWidth, x));
        this.track.style.transform = `translateX(calc(${-100 * this.physicalIndex()}% + ${distance}px))`;
      }, {signal});
      this.addEventListener('pointerup', event => {
        if (event.pointerType === 'touch' || !this.startPoint || event.pointerId !== this.startPoint.id) return;
        const x = event.clientX - this.startPoint.x;
        const y = event.clientY - this.startPoint.y;
        const move = Math.abs(x) > 50 && Math.abs(x) > Math.abs(y) * 1.5;
        this.endDrag();
        this.go(this.index + (move ? (x < 0 ? 1 : -1) : 0), move);
      }, {signal});
      this.addEventListener('pointercancel', event => {
        if (event.pointerType === 'touch' || this.startPoint?.touch) return;
        this.endDrag();
        this.go(this.index);
      }, {signal});
      this.addEventListener('lostpointercapture', () => {
        if (!this.startPoint || this.startPoint.touch) return;
        this.endDrag();
        this.go(this.index);
      }, {signal});
      this.addEventListener('touchstart', event => {
        if (this.dataset.touchSwipe === 'false' || this.slides.length < 2 || event.touches.length !== 1 || event.target.closest('button, input')) return;
        this.settleLoop();
        const touch = event.touches[0];
        this.startPoint = {x: touch.clientX, y: touch.clientY, id: touch.identifier, mouse: false, touch: true, axis: null};
        this.schedule();
      }, {signal, passive: true});
      this.addEventListener('touchmove', event => {
        if (!this.startPoint?.touch) return;
        if (event.touches.length !== 1) {
          this.endDrag();
          this.go(this.index);
          return;
        }
        const touch = [...event.touches].find(touch => touch.identifier === this.startPoint.id);
        if (!touch) return;
        const x = touch.clientX - this.startPoint.x;
        const y = touch.clientY - this.startPoint.y;
        if (this.startPoint.axis === null) {
          if (Math.max(Math.abs(x), Math.abs(y)) < 6) return;
          this.startPoint.axis = Math.abs(x) >= Math.abs(y) * 0.85 ? 'horizontal' : 'vertical';
        }
        if (this.startPoint.axis === 'vertical') return;
        if (event.cancelable) event.preventDefault();
        this.classList.add('is-dragging');
        const distance = Math.max(-this.clientWidth, Math.min(this.clientWidth, x));
        this.track.style.transform = `translateX(calc(${-100 * this.physicalIndex()}% + ${distance}px))`;
      }, {signal, passive: false});
      this.addEventListener('touchend', event => {
        if (!this.startPoint?.touch) return;
        const touch = [...event.changedTouches].find(touch => touch.identifier === this.startPoint.id);
        if (!touch) return;
        const x = touch.clientX - this.startPoint.x;
        const move = this.startPoint.axis === 'horizontal' && Math.abs(x) > 50;
        this.endDrag();
        this.go(this.index + (move ? (x < 0 ? 1 : -1) : 0), move);
      }, {signal, passive: true});
      this.addEventListener('touchcancel', () => {
        if (!this.startPoint?.touch) return;
        this.endDrag();
        this.go(this.index);
      }, {signal, passive: true});
      this.track.addEventListener('transitionend', event => {
        if (event.target === this.track && event.propertyName === 'transform') this.settleLoop();
      }, {signal});
      document.addEventListener('visibilitychange', () => {
        this.syncVideos();
        this.schedule();
      }, {signal});
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.syncVideos();
        this.schedule();
      }, {signal});
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.go(index, true);
      }, {signal});
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.slides.some(slide => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorSelected = false;
        this.schedule();
      }, {signal});
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.syncVideos();
        this.schedule();
      }, {threshold: 0.1});
      this.observer.observe(this);
      this.go(0, false, false);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.loopTimer);
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.querySelectorAll('video').forEach(video => video.pause());
    }
    go(index, manual = false, animate = true) {
      if (!this.slides.length) return;
      this.settleLoop();
      if (manual && this.dataset.resumeAfterInteraction === 'false') this.paused = true;
      const loop = this.slides.length > 1 && (index < 0 || index >= this.slides.length);
      this.index = (index + this.slides.length) % this.slides.length;
      if (!animate || this.motion.matches) this.classList.add('is-resetting');
      this.wrapPending = loop && animate && !this.motion.matches;
      const physical = this.wrapPending ? (index < 0 ? 0 : this.slides.length + 1) : this.physicalIndex();
      this.track.style.transform = `translateX(${-100 * physical}%)`;
      if (!animate || this.motion.matches) {
        this.track.getBoundingClientRect();
        this.classList.remove('is-resetting');
      }
      if (this.wrapPending) this.loopTimer = setTimeout(() => this.settleLoop(), 560);
      this.slides.forEach((slide, position) => {
        slide.inert = position !== this.index;
        if (position === this.index) slide.removeAttribute('aria-hidden');
        else slide.setAttribute('aria-hidden', 'true');
      });
      if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
      if (manual) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
      this.syncVideos();
      this.schedule();
    }
    physicalIndex() {
      return this.index + (this.slides.length > 1 ? 1 : 0);
    }
    settleLoop() {
      if (!this.wrapPending) return;
      clearTimeout(this.loopTimer);
      this.wrapPending = false;
      this.classList.add('is-resetting');
      this.track.style.transform = `translateX(${-100 * this.physicalIndex()}%)`;
      this.track.getBoundingClientRect();
      this.classList.remove('is-resetting');
    }
    schedule() {
      clearTimeout(this.timer);
      const running = !this.paused && !this.focusPaused && !this.editorSelected && this.slides.length > 1;
      if (this.toggle) {
        this.toggle.setAttribute('aria-label', running ? this.dataset.pauseLabel : this.dataset.playLabel);
        this.toggle.querySelector('[data-hero-toggle-icon]').textContent = running ? 'Ⅱ' : '▷';
      }
      if (running && !this.startPoint && !this.hovered && this.visible && !document.hidden) {
        this.timer = setTimeout(() => this.go(this.index + 1), this.interval);
      }
    }
    endDrag() {
      const pointerId = this.startPoint?.mouse ? this.startPoint.id : undefined;
      this.startPoint = null;
      this.classList.remove('is-dragging');
      if (pointerId !== undefined && this.hasPointerCapture(pointerId)) this.releasePointerCapture(pointerId);
    }
    syncVideos() {
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (index === this.index && this.visible && !document.hidden && !this.motion.matches && slide.dataset.videoPaused !== 'true') {
          video.play().then(() => this.updateVideoButton(slide)).catch(() => this.updateVideoButton(slide));
        } else {
          video.pause();
          this.updateVideoButton(slide);
        }
      });
    }
    updateVideoButton(slide) {
      const video = slide.querySelector('video');
      const button = slide.querySelector('[data-hero-video-toggle]');
      if (!button) return;
      const label = video.paused ? button.dataset.playLabel : button.dataset.pauseLabel;
      button.textContent = label;
      button.setAttribute('aria-label', label);
    }
  });
}
